import { HttpClient, RequestParams } from "../../utils/api/http-client";
import IBaseListingResponse from "../../utils/api/base-listing-response";
import { IZoneType, IZoneTypeDetails } from "../../models/wms/zone-type/response";
import { ICreateZoneType, IUpdateZoneType } from "../../models/wms/zone-type/request";
import { IZoneQuery } from "../../models/wms/zone/query";
import { IZone, IZoneDetails } from "../../models/wms/zone/response";
import { ICreateZone, IUpdateZone } from "../../models/wms/zone/request";
import { IAisleQuery } from "../../models/wms/aisle/query";
import { IAisle, IAisleDetails } from "../../models/wms/aisle/response";
import { ICreateAisle, IUpdateAisle } from "../../models/wms/aisle/request";
import { IStorageUnit, IStorageUnitDetails } from "../../models/wms/storage-unit/response";
import { ICreateStorageUnit, IUpdateStorageUnit } from "../../models/wms/storage-unit/request";
import { IShelfQuery } from "../../models/wms/shelf/query";
import { IShelf, IShelfDetails } from "../../models/wms/shelf/response";
import { ICreateShelf, IUpdateShelf } from "../../models/wms/shelf/request";
import { IBinQuery } from "../../models/wms/bin/query";
import { IBin, IBinDetails } from "../../models/wms/bin/response";
import { ICreateBin, IUpdateBin } from "../../models/wms/bin/request";
import { IWmsReportQuery } from "../../models/wms/report/query";

class WmsLayoutService<SecurityDataType = unknown> {
  http: HttpClient<SecurityDataType>;

  constructor(http: HttpClient<SecurityDataType>) {
    this.http = http;
  }

  // Zone Types
  getAllZoneTypes = (query: any, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<IZoneType>>({
      path: "/wms/zone-types",
      method: "GET",
      query,
      ...params,
    });

  getZoneType = (id: number, params?: RequestParams) =>
    this.http.request<IZoneTypeDetails>({
      path: `/wms/zone-types/${id}`,
      method: "GET",
      ...params,
    });

  createZoneType = (data: ICreateZoneType, params?: RequestParams) =>
    this.http.request({
      path: "/wms/zone-types",
      method: "POST",
      body: data,
      ...params,
    });

  updateZoneType = (id: number, data: IUpdateZoneType, params?: RequestParams) =>
    this.http.request({
      path: `/wms/zone-types/${id}`,
      method: "PUT",
      body: data,
      ...params,
    });

  deleteZoneType = (id: number, params?: RequestParams) =>
    this.http.request({
      path: `/wms/zone-types/${id}`,
      method: "DELETE",
      ...params,
    });

  // Zones
  getAllZones = (query: IZoneQuery, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<IZone>>({
      path: "/wms/zones",
      method: "GET",
      query,
      ...params,
    });

  getZone = (id: number, params?: RequestParams) =>
    this.http.request<IZoneDetails>({
      path: `/wms/zones/${id}`,
      method: "GET",
      ...params,
    });

  createZone = (data: ICreateZone, params?: RequestParams) =>
    this.http.request({
      path: "/wms/zones",
      method: "POST",
      body: data,
      ...params,
    });

  updateZone = (id: number, data: IUpdateZone, params?: RequestParams) =>
    this.http.request({
      path: `/wms/zones/${id}`,
      method: "PUT",
      body: data,
      ...params,
    });

  deleteZone = (id: number, params?: RequestParams) =>
    this.http.request({
      path: `/wms/zones/${id}`,
      method: "DELETE",
      ...params,
    });

  reorderZone = (id: number, pickSequence: number, params?: RequestParams) =>
    this.http.request({
      path: `/wms/zones/${id}/reorder`,
      method: "POST",
      body: { pickSequence },
      ...params,
    });

  toggleZoneActive = (id: number, isActive: boolean, params?: RequestParams) =>
    this.http.request({
      path: `/wms/zones/${id}/toggle-status`,
      method: "POST",
      body: { isActive },
      ...params,
    });

  // Aisles
  getAllAisles = (query: IAisleQuery, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<IAisle>>({
      path: "/wms/aisles",
      method: "GET",
      query,
      ...params,
    });

  getAisle = (id: number, params?: RequestParams) =>
    this.http.request<IAisleDetails>({
      path: `/wms/aisles/${id}`,
      method: "GET",
      ...params,
    });

  createAisle = (data: ICreateAisle, params?: RequestParams) =>
    this.http.request({
      path: "/wms/aisles",
      method: "POST",
      body: data,
      ...params,
    });

  updateAisle = (id: number, data: IUpdateAisle, params?: RequestParams) =>
    this.http.request({
      path: `/wms/aisles/${id}`,
      method: "PUT",
      body: data,
      ...params,
    });

  deleteAisle = (id: number, params?: RequestParams) =>
    this.http.request({
      path: `/wms/aisles/${id}`,
      method: "DELETE",
      ...params,
    });

  reorderAisle = (id: number, pickSequence: number, params?: RequestParams) =>
    this.http.request({
      path: `/wms/aisles/${id}/reorder`,
      method: "POST",
      body: { pickSequence },
      ...params,
    });

  // Storage Units
  getAllStorageUnits = (query: any, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<IStorageUnit>>({
      path: "/wms/storage-units",
      method: "GET",
      query,
      ...params,
    });

  getStorageUnit = (id: number, params?: RequestParams) =>
    this.http.request<IStorageUnitDetails>({
      path: `/wms/storage-units/${id}`,
      method: "GET",
      ...params,
    });

  createStorageUnit = (data: ICreateStorageUnit, params?: RequestParams) =>
    this.http.request({
      path: "/wms/storage-units",
      method: "POST",
      body: data,
      ...params,
    });

  updateStorageUnit = (id: number, data: IUpdateStorageUnit, params?: RequestParams) =>
    this.http.request({
      path: `/wms/storage-units/${id}`,
      method: "PUT",
      body: data,
      ...params,
    });

  deleteStorageUnit = (id: number, params?: RequestParams) =>
    this.http.request({
      path: `/wms/storage-units/${id}`,
      method: "DELETE",
      ...params,
    });

  reorderStorageUnit = (id: number, pickSequence: number, params?: RequestParams) =>
    this.http.request({
      path: `/wms/storage-units/${id}/reorder`,
      method: "POST",
      body: { pickSequence },
      ...params,
    });

  // Shelves
  getAllShelves = (query: IShelfQuery, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<IShelf>>({
      path: "/wms/shelves",
      method: "GET",
      query,
      ...params,
    });

  getShelf = (id: number, params?: RequestParams) =>
    this.http.request<IShelfDetails>({
      path: `/wms/shelves/${id}`,
      method: "GET",
      ...params,
    });

  createShelf = (data: ICreateShelf, params?: RequestParams) =>
    this.http.request({
      path: "/wms/shelves",
      method: "POST",
      body: data,
      ...params,
    });

  updateShelf = (id: number, data: IUpdateShelf, params?: RequestParams) =>
    this.http.request({
      path: `/wms/shelves/${id}`,
      method: "PUT",
      body: data,
      ...params,
    });

  deleteShelf = (id: number, params?: RequestParams) =>
    this.http.request({
      path: `/wms/shelves/${id}`,
      method: "DELETE",
      ...params,
    });

  reorderShelf = (id: number, pickSequence: number, params?: RequestParams) =>
    this.http.request({
      path: `/wms/shelves/${id}/reorder`,
      method: "POST",
      body: { pickSequence },
      ...params,
    });

  // Bins
  getAllBins = (query: IBinQuery, params?: RequestParams) =>
    this.http.request<IBaseListingResponse<IBin>>({
      path: "/wms/bins",
      method: "GET",
      query,
      ...params,
    });

  getBin = (id: number, params?: RequestParams) =>
    this.http.request<IBinDetails>({
      path: `/wms/bins/${id}`,
      method: "GET",
      ...params,
    });

  createBin = (data: ICreateBin, params?: RequestParams) =>
    this.http.request({
      path: "/wms/bins",
      method: "POST",
      body: data,
      ...params,
    });

  updateBin = (id: number, data: IUpdateBin, params?: RequestParams) =>
    this.http.request({
      path: `/wms/bins/${id}`,
      method: "PUT",
      body: data,
      ...params,
    });

  deleteBin = (id: number, params?: RequestParams) =>
    this.http.request({
      path: `/wms/bins/${id}`,
      method: "DELETE",
      ...params,
    });

  reorderBin = (id: number, pickSequence: number, params?: RequestParams) =>
    this.http.request({
      path: `/wms/bins/${id}/reorder`,
      method: "POST",
      body: { pickSequence },
      ...params,
    });

  // Store Activation
  activateStore = (data: { storeId: number }, params?: RequestParams) =>
    this.http.request({
      path: "/wms/activate",
      method: "POST",
      body: data,
      ...params,
    });

  // Reports
  getReconciliationReport = (query: IWmsReportQuery, params?: RequestParams) =>
    this.http.request({
      path: "/wms/reports/reconciliation",
      method: "GET",
      query,
      ...params,
    });

  getStockByLocationReport = (query: any, params?: RequestParams) =>
    this.http.request({
      path: "/wms/reports/stock-by-location",
      method: "GET",
      query,
      ...params,
    });

  getPickingPerformanceReport = (query: any, params?: RequestParams) =>
    this.http.request({
      path: "/wms/reports/picking-performance",
      method: "GET",
      query,
      ...params,
    });
}

export default WmsLayoutService;
